import type { ApiResponse } from "@/types/common";
import { privateRequest } from "../axios";

export interface GetPaymentResponse {
  totalRecord: number;
  results: Payment[];
}

export interface GetPaymentParams {
  search?: string | null;
  pageNo?: number;
  perPage?: number;
  fromDate?: string;
  toDate?: string;
  paymentStatus?: PaymentStatus;
  userType?: "INVESTOR" | "FOUNDER";
}

export type PaymentStatus = "SUCCESS" | "PENDING" | "FAILED";

export type RefundStatus =
  | "NOT_INITIATED"
  | "INITIATED"
  | "PROCESSED"
  | "FAILED";

export interface Payment {
  id: number;
  paymentStatus: PaymentStatus;
  amount: number;
  refId: string;
  paymentId: string;
  paymentType: string;
  userType: "INVESTOR" | "FOUNDER";
  createdAt: string; // ISO string (OffsetDateTime)
  businessName: string;
  message: string;
  refundStatus: RefundStatus;
  mode: string;
  firstName: string;
  lastName: string;
}

export interface PaymentSessionResponse {
  paymentUrl: string;
  sessionToken: string;
}

const ENDPOINTS = {
  PAYMENTS: "/payments",
  SUBSCRIPTION_PAYMENT: "/subscription/payment",
};

const getAllPayments = async (
  params?: GetPaymentParams,
): Promise<GetPaymentResponse> => {
  const response = await privateRequest.get<ApiResponse<GetPaymentResponse>>(
    ENDPOINTS.PAYMENTS,
    { params },
  );
  return {
    results: response?.data?.data?.results ?? [],
    totalRecord: response?.data?.data?.totalRecord ?? 0,
  };
};

const exportPayments = async (params?: Partial<GetPaymentParams>) => {
  return privateRequest.get(ENDPOINTS.PAYMENTS, {
    params: {
      ...params,
      export: true,
    },
    responseType: "blob",
  });
};

const createPaymentSession = async (): Promise<ApiResponse<PaymentSessionResponse>> => {
  const response = await privateRequest.post<ApiResponse<PaymentSessionResponse>>(
    ENDPOINTS.SUBSCRIPTION_PAYMENT,
  );
  return response.data;
};

const PaymentService = {
  getAllPayments,
  exportPayments,
  createPaymentSession,
};

export default PaymentService;
